import React from "react";
import { Box, HStack, Text, VStack, Badge, Flex, Tooltip } from "@chakra-ui/react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

type BookingStatus =
    | "pending"
    | "awaiting_owner_approval"
    | "approved"
    | "declined"
    | "paid"
    | "active"
    | "completed"
    | "cancelled"
    | "refunded";

const STATUS_FLOW: { key: BookingStatus; label: string; hint: string; }[] = [
    { key: "pending", label: "Pending", hint: "Booking created" },
    { key: "awaiting_owner_approval", label: "Owner Review", hint: "Waiting for owner decision" },
    { key: "approved", label: "Approved", hint: "Owner accepted booking" },
    { key: "paid", label: "Paid", hint: "Payment confirmed" },
    { key: "active", label: "Active", hint: "Hire in progress" },
    { key: "completed", label: "Completed", hint: "Hire finished successfully" },
];

const STATUS_META: Record<
    BookingStatus,
    { colorScheme: string; label: string; tone: string; description: string; }
> = {
    pending: {
        colorScheme: "gray",
        label: "Pending",
        tone: "gray.600",
        description: "Booking has been created and is awaiting processing.",
    },
    awaiting_owner_approval: {
        colorScheme: "yellow",
        label: "Awaiting Approval",
        tone: "yellow.700",
        description: "Owner needs to approve or decline this request.",
    },
    approved: {
        colorScheme: "green",
        label: "Approved",
        tone: "green.700",
        description: "Booking is approved and ready for payment/next steps.",
    },
    declined: {
        colorScheme: "red",
        label: "Declined",
        tone: "red.700",
        description: "Owner declined this booking request.",
    },
    paid: {
        colorScheme: "blue",
        label: "Paid",
        tone: "blue.700",
        description: "Payment was received successfully.",
    },
    active: {
        colorScheme: "purple",
        label: "Active",
        tone: "purple.700",
        description: "Hire is currently ongoing.",
    },
    completed: {
        colorScheme: "green",
        label: "Completed",
        tone: "green.700",
        description: "Hire completed successfully.",
    },
    cancelled: {
        colorScheme: "orange",
        label: "Cancelled",
        tone: "orange.700",
        description: "Booking was cancelled.",
    },
    refunded: {
        colorScheme: "teal",
        label: "Refunded",
        tone: "teal.700",
        description: "Refund has been processed.",
    },
};

function getActiveIndex(status: BookingStatus) {
    const idx = STATUS_FLOW.findIndex((s) => s.key === status);
    if (idx === -1) {
        if (status === "declined") return 1;
        if (status === "cancelled" || status === "refunded") return 3;
        return 0;
    }
    return idx;
}

export default function BookingStatusTimeline({
    status,
    compact = false,
}: {
    status: BookingStatus;
    compact?: boolean;
}) {
    const { theme } = useCustomTheme();
    const activeIndex = getActiveIndex(status);
    const meta = STATUS_META[status] || STATUS_META.pending;
    const isTerminal = ["declined", "cancelled", "refunded"].includes(status);
    const cardsBorderRadii = "8px";

    return (
        <Box
            w="100%"
            border="1px solid"
            borderColor={theme.border}
            bg={compact ? "transparent" : theme.surfaceAlt}
            borderRadius={cardsBorderRadii}
            p={compact ? 0 : 3}
        >
            <HStack justify="space-between" mb={compact ? 2 : 3} align="start">
                <VStack align="start" spacing={0}>
                    <Text fontWeight="semibold" fontSize={compact ? "sm" : "md"} color={theme.text}>
                        Booking Status
                    </Text>
                    {!compact && (
                        <Text fontSize="xs" color={theme.textSubtle}>
                            {meta.description}
                        </Text>
                    )}
                </VStack>

                <Badge
                    colorScheme={meta.colorScheme}
                    px={2.5}
                    py={1}
                    borderRadius={cardsBorderRadii}
                    textTransform="none"
                    fontSize="0.75rem"
                >
                    {meta.label}
                </Badge>
            </HStack>

            <Flex w="100%" align="center" gap={2}>
                {STATUS_FLOW.map((step, idx) => {
                    const done = idx < activeIndex;
                    const active = idx === activeIndex;
                    const showDone = isTerminal ? idx <= activeIndex : done;

                    const dotBg = active ? (theme.brand?.electricBlue || theme.primary) : showDone ? theme.textMuted : theme.borderStrong;
                    const dotBorder = active ? "2px solid" : "1px solid";
                    const dotBorderColor = active ? (theme.brand?.electricBlue || theme.primary) : showDone ? theme.textMuted : theme.borderStrong;
                    const lineBg = showDone ? (theme.brand?.electricBlue || theme.primary) : theme.border;

                    return (
                        <React.Fragment key={step.key}>
                            <VStack spacing={1} align="center" flex="1" minW={0}>
                                <Tooltip label={step.hint} hasArrow placement="top">
                                    <Box
                                        w={compact ? "11px" : "14px"}
                                        h={compact ? "11px" : "14px"}
                                        borderRadius={cardsBorderRadii}
                                        bg={dotBg}
                                        border={dotBorder}
                                        borderColor={dotBorderColor}
                                        boxShadow={active ? `0 0 0 4px ${theme.primarySoft}` : "none"}
                                        transition="all .2s ease"
                                    />
                                </Tooltip>

                                {!compact && (
                                    <Text
                                        fontSize="xs"
                                        textAlign="center"
                                        color={active ? theme.text : theme.textSubtle}
                                        fontWeight={active ? "600" : "500"}
                                        noOfLines={2}
                                    >
                                        {step.label}
                                    </Text>
                                )}
                            </VStack>

                            {idx < STATUS_FLOW.length - 1 && (
                                <Box
                                    flex="1"
                                    h={compact ? "3px" : "4px"}
                                    bg={lineBg}
                                    borderRadius={cardsBorderRadii}
                                    transition="all .2s ease"
                                />
                            )}
                        </React.Fragment>
                    );
                })}
            </Flex>

            {isTerminal && !compact && (
                <Text mt={3} fontSize="xs" color={theme.textMuted} fontWeight="medium">
                    Final state: {meta.label}.
                </Text>
            )}
        </Box>
    );
}